import React from "react";
import { api } from "@/lib/api";
import { Phone, Check, Sparkles } from "lucide-react";

/**
 * Sticky WhatsApp / Call action buttons for travel offers. Renders nothing if
 * no `booking_cta_type` is set.
 *
 * On click, ALSO fires POST /api/posts/:id/track with the matching engagement
 * subtype (WHATSAPP or CALL).
 */
export function TravelPosterCTAs({ post, className = "", size = "md" }) {
    const details = post?.details || {};
    const cta = details.booking_cta_type;
    if (!cta) return null;

    const showWa = cta === "WHATSAPP" || cta === "BOTH";
    const showCall = cta === "CALL" || cta === "BOTH";
    const wa = details.booking_whatsapp;
    const phone = details.booking_phone;
    const label = details.booking_price_label;

    const track = (engagement_type) => {
        try {
            api.post(`/posts/${post.id}/track`, {
                event_type: "ENGAGEMENT",
                engagement_type,
            }).catch(() => {});
        } catch (_) {
            /* swallow — CTA must not block */
        }
    };

    const waHref = wa
        ? `https://wa.me/91${wa}?text=${encodeURIComponent(`Hi, I saw your offer on OfferNest - ${post.title || ""}`)}`
        : "#";
    const telHref = phone ? `tel:+91${phone}` : "#";

    const sizeClasses =
        size === "sm"
            ? "h-10 text-sm"
            : "h-12 text-base";

    return (
        <div
            className={`flex flex-col gap-2 ${className}`}
            data-testid="travel-cta-wrap"
        >
            {label && (
                <div
                    className="inline-flex w-fit items-center gap-1 rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800"
                    data-testid="travel-price-label"
                >
                    <Sparkles size={12} /> {label}
                </div>
            )}
            <div className="flex gap-2">
                {showWa && wa && (
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("WHATSAPP")}
                        data-testid="travel-cta-whatsapp"
                        className={`flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 font-bold text-white shadow-md transition hover:bg-emerald-600 active:scale-[0.98] ${sizeClasses}`}
                    >
                        <WhatsAppIcon />
                        WhatsApp
                    </a>
                )}
                {showCall && phone && (
                    <a
                        href={telHref}
                        onClick={() => track("CALL")}
                        data-testid="travel-cta-call"
                        className={`flex flex-1 items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 font-bold text-white shadow-md transition hover:bg-sky-700 active:scale-[0.98] ${sizeClasses}`}
                    >
                        <Phone size={16} /> Call
                    </a>
                )}
            </div>
        </div>
    );
}

/** Simple checklist of a post's travel inclusions. Renders nothing if empty. */
export function InclusionsList({ inclusions, className = "" }) {
    if (!inclusions || !inclusions.length) return null;
    return (
        <div className={className} data-testid="inclusions-list">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-neutral-500">
                What's included
            </div>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm text-neutral-700">
                {inclusions.map((it, i) => (
                    <li
                        key={`${it}-${i}`}
                        className="flex items-start gap-1.5"
                        data-testid={`inclusion-${i}`}
                    >
                        <Check size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** WhatsApp icon (inline svg) — keeps us off yet another dependency. */
function WhatsAppIcon({ size = 16 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M17.472 14.382c-.297-.149-1.758-.868-2.03-.967-.272-.099-.47-.148-.669.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.479-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.571-.01-.198 0-.52.075-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.625.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M20.52 3.449C18.24 1.245 15.24 0 12.05 0 5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.304-1.654a11.86 11.86 0 0 0 5.68 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.52 3.449zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.26c.002-5.45 4.437-9.884 9.887-9.884a9.821 9.821 0 0 1 6.988 2.898 9.825 9.825 0 0 1 2.892 6.994c-.003 5.45-4.437 9.885-9.883 9.885z" />
        </svg>
    );
}
